import { AuthProviderProps } from 'react-oidc-context';
import applicationSettings from '../infrastructure/config';
import { WebStorageStateStore } from 'oidc-client-ts';
import { joinPaths } from '../infrastructure/path';
import { TenantId } from '../infrastructure/tenant';
import {
  isAllowedIdentityProviderId,
  isClearIdentityProviderId,
  resolveOidpid,
} from './oidpidStorage';

/**
 * Builds the acr_values string for OIDC authentication
 * @param overrideOidpid - Optional provider ID(s) to override the default. Can be a single string or array.
 * @returns The acr_values string (e.g., "oidpid:ProviderName" or "oidpid:Provider1 oidpid:Provider2") or undefined
 */
export function buildAcrValues(overrideOidpid?: string | string[] | null): string | undefined {
  const allowedProviders = applicationSettings.allowedOverrideDefaultOidcProviderIds ?? [];
  const normalizeOverride = (value?: string | string[] | null): string[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  const idps = overrideOidpid
    ? normalizeOverride(overrideOidpid)
    : (() => {
        const resolved = resolveOidpid(allowedProviders);
        return resolved ? [resolved] : [];
      })();

  // Ignore unknown providers; clr means reset to default
  const containsClear = idps.some((idp) => isClearIdentityProviderId(idp));
  const allowedIdps = idps.filter(
    (idp) => !isClearIdentityProviderId(idp) && isAllowedIdentityProviderId(idp, allowedProviders)
  );

  if (allowedIdps.length > 0) {
    // acr_values accepts space-separated providers
    return allowedIdps.map((p) => `idp:${p}`).join(' ');
  }

  if (containsClear) {
    return undefined;
  }

  // Fallback to default provider from tenant config
  if (applicationSettings.oidcDefaultProviderId?.length > 0) {
    return `idp:${applicationSettings.oidcDefaultProviderId}`;
  }

  return undefined;
}

export const oidcConfig = (): AuthProviderProps => {
  const tenantUrl = joinPaths(window.location.origin, TenantId);

  return {
    authority: applicationSettings.oidcServiceUrl,
    client_id: applicationSettings.oidcClientId,
    redirect_uri: tenantUrl,
    post_logout_redirect_uri: tenantUrl,
    scope: applicationSettings.oidcScope,
    acr_values: buildAcrValues(),
    response_type: 'code',
    response_mode: 'query',
    monitorSession: false,
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    onSigninCallback: (): void => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
};
